import type { Promisable } from "@core/foundation/promise";
import type { BrandedString } from "@core/foundation/string";
import type { ZodObject } from "@core/zod";
import { z } from "@core/zod";

import type {
  JsonPaginatedResult,
  JsonPaginationOptions,
  JsonPayload,
  JsonUpdate,
  JsonUpdateOptions,
} from "./JsonRepositoryContract.ts";
import type { JsonTransactionContract } from "./JsonTransactionContract.ts";

const PK_SYMBOL = Symbol("PK");

const SK_SYMBOL = Symbol("SK");

const SK2_SYMBOL = Symbol("SK2");

export const SK_VERSION_STRING = { v0: "v0" } as const;

export const ETAG_FIELD = "etag";

export const NoIdentifierSchema = z.object(
  { unused: z.never().optional() },
  { unused: "The placeholder of a record whose keys carry no identifier" },
);

export type PK<EntityType extends string> = BrandedString<
  typeof PK_SYMBOL,
  `${EntityType}#${string}` | EntityType
>;

export type SK = BrandedString<typeof SK_SYMBOL>;

export type SK2 = BrandedString<typeof SK2_SYMBOL>;

export interface JsonCreate<
  Identifier extends ZodObject,
  Data extends ZodObject,
  EntityType extends string,
> {
  readonly PK: PK<EntityType>;
  readonly SK: SK;
  readonly SK2?: SK2;
  readonly payload: JsonPayload<Identifier, Data>;
}

export interface JsonRepositoryHelperContract<
  Identifier extends ZodObject,
  Data extends ZodObject,
  Transaction extends JsonTransactionContract,
  EntityType extends string,
> {
  readonly __typeTag: "JsonRepositoryHelperContract";
  /**
   * @throws {JsonDuplicateError} On duplicate.
   */
  readonly create: (
    create: JsonCreate<Identifier, Data, EntityType>,
  ) => Promisable<JsonPayload<Identifier, Data>>;
  readonly entityType: EntityType;
  readonly getById: (
    PK: PK<EntityType>,
    SK: SK,
  ) => Promisable<JsonPayload<Identifier, Data> | null>;
  readonly getByIds: (
    keys: readonly { readonly PK: PK<EntityType>; readonly SK: SK }[],
  ) => Promisable<readonly JsonPayload<Identifier, Data>[]>;
  readonly getByPK: (
    PK: PK<EntityType>,
    options?: JsonPaginationOptions,
  ) => Promisable<JsonPaginatedResult<Identifier, Data>>;
  readonly getBySKPrefix: (
    PK: PK<EntityType>,
    SKPrefix: SK,
    options?: JsonPaginationOptions,
  ) => Promisable<JsonPaginatedResult<Identifier, Data>>;
  readonly prepareCreate: (
    transaction: Transaction,
    create: JsonCreate<Identifier, Data, EntityType>,
  ) => Promisable<void>;
  readonly prepareUpdateById: (
    transaction: Transaction,
    PK: PK<EntityType>,
    SK: SK,
    update: JsonUpdate<Data>,
    options?: JsonUpdateOptions,
  ) => Promisable<void>;
  /**
   * @throws {JsonWriteConflictError} If the stored etag differs from the
   *   one the options name.
   */
  readonly updateById: (
    PK: PK<EntityType>,
    SK: SK,
    update: JsonUpdate<Data>,
    options?: JsonUpdateOptions,
  ) => Promisable<JsonPayload<Identifier, Data> | null>;
}
